import * as ToastPrimitive from '@radix-ui/react-toast';
import { CheckCircle2, Info, TriangleAlert, XCircle } from 'lucide-react';
import { useEffect, type ReactNode } from 'react';

import { cn } from '@/shared/lib/cn';
import { useToastStore, type ToastVariant } from '@/stores/toast.store';

const variantIcon: Record<ToastVariant, ReactNode> = {
  default: <Info size={18} aria-hidden />,
  success: <CheckCircle2 size={18} aria-hidden />,
  danger: <XCircle size={18} aria-hidden />,
  warning: <TriangleAlert size={18} aria-hidden />,
};

const variantClass: Record<ToastVariant, string> = {
  default: 'bg-surface-highest text-on-surface',
  success: 'bg-income-green/15 text-income-green ring-1 ring-income-green/30',
  danger: 'bg-danger/15 text-danger ring-1 ring-danger/30',
  warning: 'bg-warning/15 text-warning ring-1 ring-warning/30',
};

export function ToastProvider({ children }: { children: ReactNode }) {
  return (
    <ToastPrimitive.Provider swipeDirection="right" duration={4000}>
      {children}
      <ToastViewport />
      <ToastList />
    </ToastPrimitive.Provider>
  );
}

function ToastViewport() {
  return (
    <ToastPrimitive.Viewport
      className={cn(
        'fixed z-[var(--z-toast)] flex max-w-[420px] flex-col gap-2 outline-none',
        'right-4 top-4 sm:right-6 sm:top-6',
      )}
    />
  );
}

function ToastList() {
  const toasts = useToastStore((s) => s.toasts);
  const dismiss = useToastStore((s) => s.dismiss);
  return (
    <>
      {toasts.map((t) => (
        <ToastItemView
          key={t.id}
          message={t.message}
          variant={t.variant}
          duration={t.duration}
          onClose={() => dismiss(t.id)}
        />
      ))}
    </>
  );
}

function ToastItemView({
  message,
  variant,
  duration,
  onClose,
}: {
  message: string;
  variant: ToastVariant;
  duration: number;
  onClose: () => void;
}) {
  useEffect(() => {
    const t = setTimeout(onClose, duration);
    return () => clearTimeout(t);
  }, [duration, onClose]);

  return (
    <ToastPrimitive.Root
      open
      onOpenChange={(o) => !o && onClose()}
      duration={duration}
      data-testid="toast-item"
      className={cn(
        'flex animate-fade-in items-start gap-3 rounded-card px-4 py-3 shadow-modal',
        variantClass[variant],
      )}
    >
      <span className="mt-0.5">{variantIcon[variant]}</span>
      <ToastPrimitive.Description className="flex-1 text-body-md">
        {message}
      </ToastPrimitive.Description>
      <ToastPrimitive.Close
        aria-label="Fechar notificação"
        className="rounded-pill p-1 text-secondary-text hover:bg-surface-high"
      >
        <XCircle size={16} aria-hidden />
      </ToastPrimitive.Close>
    </ToastPrimitive.Root>
  );
}
